import { useEffect, useState } from "react";
import { useAppStore } from "../../store/app";
import { getConsultant, upsertConsultant, type ConsultantDetail, type Seniority } from "../../lib/api/consultants-v2";
import { fetchUserPhoto } from "../../lib/graph/photo";
import ConsultantAvatar from "../shared/ConsultantAvatar";

// Minimal greenfield dossier. Displays Entra identity + Lens-side status
// fields with edit affordances. Placement/notes/CV/section tabs will be
// wired up as their endpoints get consumed in later iterations.
//
// Prop naming: `folder` is retained for call-site backward compat with the
// URL routing pattern in App.tsx, but the value is now an Entra OID.

interface Props {
  folder: string;               // actually the consultant's Entra OID
  onBack: () => void;
}

const SENIORITY_OPTIONS: (Seniority | "")[] = ["", "junior", "medior", "senior", "lead", "principal"];

export default function ConsultantDossier({ folder: oid, onBack }: Props) {
  const accessToken = useAppStore((s) => s.accessToken);

  const [detail, setDetail] = useState<ConsultantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const data = await getConsultant(oid);
        if (alive) setDetail(data);
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : "Failed to load consultant");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [oid]);

  useEffect(() => {
    const email = detail?.identity?.mail;
    if (!accessToken || !email) return;
    let alive = true;
    fetchUserPhoto(accessToken, email).then((url) => {
      if (!alive) { if (url) URL.revokeObjectURL(url); return; }
      setPhotoUrl(url);
    });
    return () => { alive = false; };
  }, [accessToken, detail?.identity?.mail]);

  async function persist(patch: { seniority?: Seniority | null; nonConsulting?: boolean; active?: boolean }) {
    setSaving(true);
    try {
      const row = await upsertConsultant(oid, patch);
      setDetail((prev) => prev ? { ...prev, lensRow: row ?? prev.lensRow } : prev);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  const identity = detail?.identity;
  const lensRow = detail?.lensRow;
  const displayName = identity?.displayName ?? lensRow?.entraDisplayName ?? "(no name)";

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8 space-y-6">
      <button
        onClick={onBack}
        className="text-xs text-slate-400 hover:text-indigo-600 transition-colors cursor-pointer focus:outline-none block"
      >
        ← People
      </button>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <span className="h-7 w-7 animate-spin rounded-full border-2 border-slate-300 border-t-slate-800" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : detail ? (
        <>
          {/* Header */}
          <div className="flex items-center gap-4">
            <ConsultantAvatar name={displayName} photoUrl={photoUrl} size="lg" />
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                {displayName}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {identity?.mail ?? "(no email)"}
                {identity?.jobTitle && <span> · {identity.jobTitle}</span>}
              </p>
              <p className="text-[10px] font-mono text-slate-400 mt-0.5">{oid}</p>
            </div>
          </div>

          {/* Identity card (from Entra — read-only) */}
          <section className="rounded-2xl border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-[#182132] p-5">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Identity (Entra)</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><dt className="text-[10px] uppercase text-slate-400">Given name</dt><dd>{identity?.givenName ?? "—"}</dd></div>
              <div><dt className="text-[10px] uppercase text-slate-400">Surname</dt><dd>{identity?.surname ?? "—"}</dd></div>
              <div><dt className="text-[10px] uppercase text-slate-400">UPN</dt><dd className="font-mono text-xs">{identity?.userPrincipalName ?? "—"}</dd></div>
              <div><dt className="text-[10px] uppercase text-slate-400">Mobile</dt><dd>{identity?.mobilePhone ?? "—"}</dd></div>
              <div><dt className="text-[10px] uppercase text-slate-400">Office</dt><dd>{identity?.officeLocation ?? "—"}</dd></div>
            </dl>
            {!identity && (
              <p className="text-xs text-amber-600">This consultant exists in Lens but was not found in Entra (former staff).</p>
            )}
          </section>

          {/* Lens-side fields (editable) */}
          <section className="rounded-2xl border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-[#182132] p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400">Lens</h2>
              {!lensRow && <span className="text-[10px] text-slate-400">No Lens row yet — saves will create one</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase text-slate-400">Seniority</span>
                <select
                  disabled={saving}
                  value={lensRow?.seniority ?? ""}
                  onChange={(e) => void persist({ seniority: (e.target.value || null) as Seniority | null })}
                  className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5"
                >
                  {SENIORITY_OPTIONS.map((s) => (
                    <option key={s || "none"} value={s}>{s || "—"}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 pt-5">
                <input
                  type="checkbox"
                  disabled={saving}
                  checked={lensRow?.active ?? true}
                  onChange={(e) => void persist({ active: e.target.checked })}
                />
                <span className="text-sm">Active</span>
              </label>
              <label className="flex items-center gap-2 pt-5">
                <input
                  type="checkbox"
                  disabled={saving}
                  checked={lensRow?.nonConsulting ?? false}
                  onChange={(e) => void persist({ nonConsulting: e.target.checked })}
                />
                <span className="text-sm">Non-consulting</span>
              </label>
            </div>
          </section>

          {/* Placeholder for section-based CV content */}
          <section className="rounded-2xl border border-dashed border-slate-200/70 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-900/40 p-5">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Profile sections</h2>
            <p className="text-sm text-slate-500">
              {Object.keys(detail.currentSections).length === 0
                ? "No profile sections yet. Work Experience, Skills, Education etc. will land here once section-authoring UI is wired."
                : `${Object.keys(detail.currentSections).length} section(s) present — viewer to come.`}
            </p>
          </section>
        </>
      ) : null}
    </div>
  );
}
